// src/components/creators/CreatorsAdmin.js
// Admin view of all creators: branding (logo + social links), referral code,
// Stripe Connect state, referral aggregates, and per-creator referral detail.
// Plus the "Become a Creator" enquiry queue from the storefront — approving
// an enquiry flips users.is_content_creator via the same endpoint User
// Management uses.
import React, { useCallback, useEffect, useState } from 'react';
import { AtSign, Camera, ExternalLink, Globe, Music2, Play } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from 'components/ui/alert';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/ui/table';
import LoadingState from 'components/shared/LoadingState';
import { cn } from 'lib/utils';
import { formatCurrency } from 'utils/format';
import {
  getCreators,
  getCreatorReferrals,
  getCreatorEnquiries,
  setCreatorEnquiryStatus,
  setContentCreatorStatus,
} from '../../services/api';

// Same icon stand-ins as the creator app's ReferralCentre (lucide has no brand icons)
const SOCIAL_ICONS = [
  { key: 'twitter', label: 'Twitter / X', Icon: AtSign },
  { key: 'youtube', label: 'YouTube', Icon: Play },
  { key: 'instagram', label: 'Instagram', Icon: Camera },
  { key: 'tiktok', label: 'TikTok', Icon: Music2 },
  { key: 'website', label: 'Website', Icon: Globe },
];

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

function CreatorLogo({ creator, size = 'h-9 w-9' }) {
  const [errored, setErrored] = useState(false);
  const initial = (creator.username || creator.email || '?')[0]?.toUpperCase();

  if (!creator.logo_url || errored) {
    return (
      <span
        className={cn(
          size,
          'flex shrink-0 items-center justify-center rounded-full bg-secondary text-token-md font-semibold text-secondary-foreground'
        )}
      >
        {initial}
      </span>
    );
  }
  return (
    <img
      src={creator.logo_url}
      alt={`${creator.username || creator.email || 'Creator'} logo`}
      className={cn(size, 'shrink-0 rounded-full border border-borderGrey object-cover')}
      onError={() => setErrored(true)}
    />
  );
}

// Only link out to http(s) URLs — DB values are creator-supplied
const isSafeUrl = (url) => /^https?:\/\//i.test(url || '');

function SocialLinks({ links, emptyText = '-' }) {
  const present = SOCIAL_ICONS.filter(({ key }) => isSafeUrl(links?.[key]));
  if (present.length === 0) {
    return <span className="text-muted-foreground">{emptyText}</span>;
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      {present.map(({ key, label, Icon }) => (
        <a
          key={key}
          href={links[key]}
          target="_blank"
          rel="noopener noreferrer"
          title={`${label}: ${links[key]}`}
          onClick={(e) => e.stopPropagation()}
          className="rounded-token-medium p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
          <span className="sr-only">{label}</span>
        </a>
      ))}
    </span>
  );
}

const getConnectBadge = (connect) => {
  if (connect?.payouts_enabled) return { variant: 'success', label: 'Payouts enabled' };
  if (connect?.details_submitted) return { variant: 'warning', label: 'Verifying' };
  if (connect?.has_account) return { variant: 'warning', label: 'Onboarding' };
  return { variant: 'outline', label: 'Not connected' };
};

const getReferralStatusBadge = (status) => {
  switch (status) {
    case 'converted':
      return 'success';
    case 'disqualified':
      return 'destructive';
    case 'pending':
    default:
      return 'outline';
  }
};

const getPayoutStatusBadge = (status) => {
  switch (status) {
    case 'paid':
      return 'success';
    case 'failed':
    case 'reversed':
      return 'destructive';
    case 'pending':
    default:
      return 'outline';
  }
};

const getEnquiryStatusBadge = (status) => {
  switch (status) {
    case 'approved':
      return 'success';
    case 'dismissed':
      return 'outline';
    case 'new':
    default:
      return 'info';
  }
};

function EnquiriesSection({
  enquiries,
  loading,
  error,
  onRetry,
  onApprove,
  onDismiss,
  actioningId,
  showAll,
  onToggleShowAll,
}) {
  const newCount = enquiries.filter((e) => e.status === 'new').length;
  const visible = showAll ? enquiries : enquiries.filter((e) => e.status === 'new');

  return (
    <div className="mb-6">
      {error && (
        <Alert variant="destructive" className="mb-4 flex items-center justify-between gap-4">
          <AlertDescription>{error}</AlertDescription>
          <Button variant="outline" size="sm" onClick={onRetry}>
            Retry
          </Button>
        </Alert>
      )}

      <div className="overflow-auto rounded-token-large border border-borderGrey bg-white">
        <div className="flex items-center justify-between gap-4 border-b border-borderGrey px-4 py-3">
          <h3 className="flex items-center gap-2 text-token-lg font-semibold">
            Enquiries
            {newCount > 0 && <Badge variant="info">{newCount} new</Badge>}
          </h3>
          <Button variant="ghost" size="sm" onClick={onToggleShowAll}>
            {showAll ? 'Show new only' : 'Show all'}
          </Button>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Received</TableHead>
              <TableHead>Applicant</TableHead>
              <TableHead>Content Link</TableHead>
              <TableHead>Message</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={6}>
                  <LoadingState message="Loading enquiries..." minHeight={120} />
                </TableCell>
              </TableRow>
            ) : visible.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={6}
                  className="py-8 text-center text-token-md text-muted-foreground"
                >
                  {enquiries.length === 0
                    ? 'No enquiries yet — they arrive from the storefront Become a Creator page'
                    : 'No new enquiries'}
                </TableCell>
              </TableRow>
            ) : (
              visible.map((enquiry) => {
                const actioning = actioningId === enquiry.id;
                return (
                  <TableRow key={enquiry.id} className="hover:bg-transparent">
                    <TableCell className="whitespace-nowrap">
                      {formatDate(enquiry.created_at)}
                    </TableCell>
                    <TableCell>
                      <p className="text-token-md font-medium">{enquiry.name || 'Unknown'}</p>
                      <p className="text-token-base text-muted-foreground">
                        {enquiry.email || `WP ID: ${enquiry.wp_id}`}
                      </p>
                    </TableCell>
                    <TableCell>
                      {isSafeUrl(enquiry.content_link) ? (
                        <a
                          href={enquiry.content_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={enquiry.content_link}
                          className="inline-flex max-w-[260px] items-center gap-1 text-primary underline-offset-2 hover:underline"
                        >
                          <span className="truncate">
                            {enquiry.content_link.replace(/^https?:\/\/(www\.)?/i, '')}
                          </span>
                          <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        </a>
                      ) : (
                        <span className="text-muted-foreground">{enquiry.content_link || '-'}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <p
                        className="max-w-[320px] truncate text-token-base text-muted-foreground"
                        title={enquiry.message || undefined}
                      >
                        {enquiry.message || '-'}
                      </p>
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1.5">
                        <Badge variant={getEnquiryStatusBadge(enquiry.status)}>
                          {enquiry.status}
                        </Badge>
                        {enquiry.is_content_creator && enquiry.status !== 'approved' && (
                          <Badge variant="outline">Already creator</Badge>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {enquiry.status === 'new' && (
                        <span className="inline-flex items-center gap-2">
                          <Button
                            size="sm"
                            disabled={actioning}
                            onClick={() => onApprove(enquiry)}
                          >
                            Approve
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={actioning}
                            onClick={() => onDismiss(enquiry)}
                          >
                            Dismiss
                          </Button>
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function CreatorDetail({ creator, detail, detailLoading, detailError, onRetry }) {
  return (
    <div className="mt-6">
      {/* Selected creator header */}
      <div className="mb-4 flex flex-wrap items-center gap-4 rounded-token-large border border-borderGrey bg-white p-4">
        <CreatorLogo creator={creator} size="h-14 w-14" />
        <div className="min-w-0">
          <p className="text-token-lg font-semibold">
            {creator.username || creator.name || creator.email || `WP ID ${creator.wp_id}`}
          </p>
          <p className="text-token-sm text-muted-foreground">
            {creator.email || `WP ID: ${creator.wp_id}`}
            {creator.referral_code && <> · code <span className="font-mono">{creator.referral_code}</span></>}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <SocialLinks links={creator.social_links} emptyText="No social links" />
        </div>
      </div>

      {detailError && (
        <Alert variant="destructive" className="mb-4 flex items-center justify-between gap-4">
          <AlertDescription>{detailError}</AlertDescription>
          <Button variant="outline" size="sm" onClick={onRetry}>
            Retry
          </Button>
        </Alert>
      )}

      {/* Referrals */}
      <div className="overflow-auto rounded-token-large border border-borderGrey bg-white">
        <div className="border-b border-borderGrey px-4 py-3">
          <h3 className="text-token-lg font-semibold">Referrals</h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Customer</TableHead>
              <TableHead>Signed Up</TableHead>
              <TableHead>First Paid</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Subscribed</TableHead>
              <TableHead className="text-right">Invoices</TableHead>
              <TableHead className="text-right">Gross Revenue</TableHead>
              <TableHead className="text-right">Commission</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {detailLoading ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={8}>
                  <LoadingState message="Loading referrals..." minHeight={120} />
                </TableCell>
              </TableRow>
            ) : (detail?.referrals?.length ?? 0) === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={8}
                  className="py-8 text-center text-token-md text-muted-foreground"
                >
                  No referrals yet
                </TableCell>
              </TableRow>
            ) : (
              detail.referrals.map((referral) => (
                <TableRow key={referral.referred_wp_id ?? referral.email}>
                  <TableCell>
                    <p className="text-token-md font-medium">{referral.name || 'Unknown'}</p>
                    <p className="text-token-base text-muted-foreground">
                      {referral.email || `WP ID: ${referral.referred_wp_id}`}
                    </p>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">{formatDate(referral.signed_up_at)}</TableCell>
                  <TableCell className="whitespace-nowrap">{formatDate(referral.first_paid_at)}</TableCell>
                  <TableCell>
                    <Badge
                      variant={getReferralStatusBadge(referral.status)}
                      title={referral.disqualified_reason || undefined}
                    >
                      {referral.status || 'unknown'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={referral.has_active_subscription ? 'success' : 'outline'}>
                      {referral.has_active_subscription ? 'Active' : 'No'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{referral.invoice_count}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCurrency(referral.gross_revenue_gbp ?? 0)}
                  </TableCell>
                  <TableCell className="text-right font-semibold tabular-nums">
                    {formatCurrency(referral.commission_gbp ?? 0)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Payouts */}
      {(detail?.payouts?.length ?? 0) > 0 && (
        <div className="mt-4 overflow-auto rounded-token-large border border-borderGrey bg-white">
          <div className="border-b border-borderGrey px-4 py-3">
            <h3 className="text-token-lg font-semibold">Payouts</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Week Ending</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Paid At</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {detail.payouts.map((payout, index) => (
                <TableRow key={`${payout.week_ending}-${index}`}>
                  <TableCell className="whitespace-nowrap">{formatDate(payout.week_ending)}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCurrency(payout.amount_gbp ?? 0)}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={getPayoutStatusBadge(payout.status)}
                      title={payout.failure_reason || undefined}
                    >
                      {payout.status || 'unknown'}
                    </Badge>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">{formatDate(payout.paid_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export default function CreatorsAdmin() {
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selectedWpId, setSelectedWpId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [enquiriesLoading, setEnquiriesLoading] = useState(true);
  const [enquiriesError, setEnquiriesError] = useState(null);
  const [showAllEnquiries, setShowAllEnquiries] = useState(false);
  const [actioningEnquiryId, setActioningEnquiryId] = useState(null);

  const fetchCreators = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await getCreators();
      setCreators(response.creators || []);
      // Auto-select the top creator so the page opens with detail visible
      setSelectedWpId((prev) => prev ?? response.creators?.[0]?.wp_id ?? null);
    } catch (err) {
      setLoadError(err.message || 'Failed to load creators');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDetail = useCallback(async (wp_id) => {
    if (!wp_id) return;
    setDetailLoading(true);
    setDetailError(null);
    try {
      const response = await getCreatorReferrals(wp_id);
      setDetail(response);
    } catch (err) {
      setDetail(null);
      setDetailError(err.message || 'Failed to load referral detail');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const fetchEnquiries = useCallback(async () => {
    setEnquiriesLoading(true);
    setEnquiriesError(null);
    try {
      const response = await getCreatorEnquiries();
      setEnquiries(response.enquiries || []);
    } catch (err) {
      setEnquiriesError(err.message || 'Failed to load enquiries');
    } finally {
      setEnquiriesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCreators();
    fetchEnquiries();
  }, [fetchCreators, fetchEnquiries]);

  useEffect(() => {
    fetchDetail(selectedWpId);
  }, [selectedWpId, fetchDetail]);

  const handleApproveEnquiry = async (enquiry) => {
    setActioningEnquiryId(enquiry.id);
    try {
      if (!enquiry.is_content_creator) {
        await setContentCreatorStatus({ wp_id: enquiry.wp_id, is_content_creator: true });
      }
      await setCreatorEnquiryStatus({ id: enquiry.id, status: 'approved' });
      toast.success(`${enquiry.email || enquiry.name || enquiry.wp_id} is now a creator`);
      fetchEnquiries();
      fetchCreators();
    } catch (err) {
      toast.error(err.message || 'Failed to approve enquiry');
    } finally {
      setActioningEnquiryId(null);
    }
  };

  const handleDismissEnquiry = async (enquiry) => {
    setActioningEnquiryId(enquiry.id);
    try {
      await setCreatorEnquiryStatus({ id: enquiry.id, status: 'dismissed' });
      fetchEnquiries();
    } catch (err) {
      toast.error(err.message || 'Failed to dismiss enquiry');
    } finally {
      setActioningEnquiryId(null);
    }
  };

  const selectedCreator = creators.find((c) => c.wp_id === selectedWpId) || null;

  return (
    <div>
      <h2 className="text-token-xl font-semibold">Creators</h2>
      <p className="mb-6 text-sm text-muted-foreground">
        Become-a-Creator enquiries from the storefront, then every creator with their branding,
        referral code, Stripe Connect state and referral earnings. Select a creator to see their
        individual referrals and payouts.
      </p>

      <EnquiriesSection
        enquiries={enquiries}
        loading={enquiriesLoading}
        error={enquiriesError}
        onRetry={fetchEnquiries}
        onApprove={handleApproveEnquiry}
        onDismiss={handleDismissEnquiry}
        actioningId={actioningEnquiryId}
        showAll={showAllEnquiries}
        onToggleShowAll={() => setShowAllEnquiries((prev) => !prev)}
      />

      {loadError && (
        <Alert variant="destructive" className="mb-4 flex items-center justify-between gap-4">
          <AlertDescription>{loadError}</AlertDescription>
          <Button variant="outline" size="sm" onClick={fetchCreators}>
            Retry
          </Button>
        </Alert>
      )}

      <div className="overflow-auto rounded-token-large border border-borderGrey bg-white">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Creator</TableHead>
              <TableHead>Code</TableHead>
              <TableHead>Links</TableHead>
              <TableHead>Stripe Connect</TableHead>
              <TableHead className="text-right">Referrals</TableHead>
              <TableHead className="text-right">Subscribed</TableHead>
              <TableHead className="text-right">Earned</TableHead>
              <TableHead className="text-right">Unpaid</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={8}>
                  <LoadingState message="Loading creators..." minHeight={160} />
                </TableCell>
              </TableRow>
            ) : creators.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={8}
                  className="py-10 text-center text-token-md text-muted-foreground"
                >
                  No creators found
                </TableCell>
              </TableRow>
            ) : (
              creators.map((creator) => {
                const connectBadge = getConnectBadge(creator.connect);
                return (
                  <TableRow
                    key={creator.wp_id}
                    onClick={() => setSelectedWpId(creator.wp_id)}
                    className={cn(
                      'cursor-pointer',
                      creator.wp_id === selectedWpId && 'bg-brand-purple1/5 hover:bg-brand-purple1/10'
                    )}
                  >
                    <TableCell>
                      <span className="flex items-center gap-3">
                        <CreatorLogo creator={creator} />
                        <span className="min-w-0">
                          <p className="truncate text-token-md font-medium">
                            {creator.username || creator.name || `WP ID ${creator.wp_id}`}
                          </p>
                          <p className="truncate text-token-base text-muted-foreground">
                            {creator.email || `WP ID: ${creator.wp_id}`}
                          </p>
                        </span>
                      </span>
                    </TableCell>
                    <TableCell>
                      {creator.referral_code ? (
                        <Badge variant="outline" className="font-mono">
                          {creator.referral_code}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <SocialLinks links={creator.social_links} />
                    </TableCell>
                    <TableCell>
                      <Badge variant={connectBadge.variant}>{connectBadge.label}</Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {creator.lifetime_referrals}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {creator.currently_subscribed}
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">
                      {formatCurrency(creator.lifetime_commission_gbp ?? 0)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(creator.unpaid_commission_gbp ?? 0)}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {selectedCreator && (
        <CreatorDetail
          creator={selectedCreator}
          detail={detail}
          detailLoading={detailLoading}
          detailError={detailError}
          onRetry={() => fetchDetail(selectedWpId)}
        />
      )}
    </div>
  );
}
